/**
 * The report-download lead log — `GET /report-download/leads`.
 *
 * ⚠️ **`ProposalsPanel`'s shape, deliberately.** Same three views, same honesty
 * rules, same gates; the reasoning for each is written up at length there and
 * is not repeated here. What differs is the record: a report lead names a
 * PERSON and the report they downloaded, carries UTM tags, and asks for no
 * service.
 *
 * ══ 🔴 THE RULES CARRIED ACROSS ═══════════════════════════════════════════
 *
 *  - **A FAILED READ PRINTS NO COUNT.** `rows === null` is *we could not ask*;
 *    `[]` is *we asked and there are none*. The count renders only over an
 *    array and sits in no live region.
 *  - **A `403` IS A REFUSAL, NOT A FAILURE**, split on the STATUS and never on
 *    the message. It replaces the content rather than sitting above an empty
 *    table that would read as *nobody has downloaded anything*.
 *  - **Live / Archived / All**, opening on Live, mapped onto the server's
 *    tri-state `archived`: `false`, `true`, and **the key OMITTED**. The pills
 *    and the filter are one change — `Unarchive` and `Delete permanently`
 *    require an archived row.
 *  - **Every gate is here, not at the call site**, so the table and the phone
 *    card cannot disagree: archive on the read grant, delete ADMIN and only on
 *    an archived row, convert on `canCreate('deals')`.
 *  - **UTM tags: a missing tag is an em dash.** ⛔ Never "Direct", "Unknown"
 *    or any other label nobody recorded.
 *
 * ⛔ **NO HELPER TEXT.** The sentences here are the error block, the refusal,
 * the empty states and the `ConfirmDialog` body naming a destructive
 * consequence.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  ENQUIRY_ACTIONS_COLUMN_WIDTH,
  EnquiryRowActions,
  type EnquiryActionItem,
} from '@/components/enquiries/EnquiryRowActions';
import {
  Badge,
  Button,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  FilterableTH,
  FilterSheet,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type ColumnFilterOption,
  type FilterableTHProps,
  type FrozenSide,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { EnquiryAssignmentCell } from '@/components/enquiries/EnquiryAssignmentCell';
import { ApiError, api } from '@/lib/api';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { formatDateTime, pluralise } from '@/lib/format';
import type {
  EnquiryAssignment,
  ReportDownloadLead,
  ReportDownloadLeadListParams,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { EventInterestIcon } from './icons';
/* The Assigned column's shared helpers — one copy for the three Enquiries tables. */
import {
  ASSIGNEE_PARAM,
  ENQUIRY_ASSIGNMENT_COLUMN_WIDTH,
  assigneeFilterOf,
  assignmentColumnSpec,
} from '@/components/enquiries/enquiry-assignment-filter';
import { reportLeadIsConvertible, reportLeadPersonName } from './ReportDownloadConvertFlow';

/* -------------------------------------------------------------------------- */
/* The view model                                                             */
/* -------------------------------------------------------------------------- */

/** One lead, as this panel renders it. Every wire field is spelled once, in {@link rowOf}. */
export interface ReportDownloadRow {
  id: number;
  personName: string;
  /** The lead's own parts, as sent — used to prefill a contact without re-splitting. */
  firstName: string;
  lastName: string;
  email: string;
  designation: string | null;
  phone: string | null;
  /** Free text, as typed. ⛔ Never matched, joined or deduplicated on. */
  organisation: string;
  /** The report's title where the sender sent one, otherwise its slug — never an invented title. */
  reportLabel: string;
  contentSlug: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  /** The SENDER's stamp. */
  submittedAt: string;
  sourceReference: string;
  archived: boolean;
  /** Carried to WORD the row. ⛔ Not the gate — read {@link convertible}. */
  dealId: number | null;
  /** The gate, computed once from the shared predicate. */
  convertible: boolean;
  /** The EMERGE company this lead is linked to, or `null` when none is. */
  company: { id: number; name: string } | null;
  /** `null` unassigned; `undefined` an older server — a dash, never "Unassigned". */
  assignment: EnquiryAssignment | null | undefined;
}

/** A blank string is not a tag. `typeof`, because an absent key reads `undefined`. */
function tagOf(raw: unknown): string | null {
  return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null;
}

function rowOf(lead: ReportDownloadLead): ReportDownloadRow {
  const title = typeof lead.contentTitle === 'string' ? lead.contentTitle.trim() : '';
  return {
    id: lead.id,
    personName: reportLeadPersonName(lead),
    firstName: (lead.firstName ?? '').trim(),
    lastName: typeof lead.lastName === 'string' ? lead.lastName.trim() : '',
    email: lead.email,
    designation: lead.designation,
    phone: lead.phone,
    organisation: lead.organisation,
    reportLabel: title === '' ? lead.contentSlug : title,
    contentSlug: lead.contentSlug,
    utmSource: tagOf(lead.utmSource),
    utmMedium: tagOf(lead.utmMedium),
    utmCampaign: tagOf(lead.utmCampaign),
    submittedAt: lead.submittedAt,
    sourceReference: lead.sourceReference,
    /* ⛔ `typeof`, never `!== null` — see B2bProposalSubmission.archivedAt. */
    archived: typeof lead.archivedAt === 'string',
    dealId: typeof lead.dealId === 'number' ? lead.dealId : null,
    convertible: reportLeadIsConvertible(lead),
    /* An API predating the key sends nothing, which reads as no company. */
    company: lead.company ? { id: lead.company.id, name: lead.company.name } : null,
    assignment: lead.assignment,
  };
}

/* -------------------------------------------------------------------------- */
/* The three views                                                            */
/* -------------------------------------------------------------------------- */

export type ReportDownloadView = 'live' | 'archived' | 'all';

interface ViewSpec {
  value: ReportDownloadView;
  label: string;
  singular: string;
  plural: string;
  emptyTitle: string;
  /** ⛔ Never implies data is on its way. */
  emptyDescription: string;
}

const LIVE_VIEW: ViewSpec = {
  value: 'live',
  label: 'Live',
  singular: 'live lead',
  plural: 'live leads',
  emptyTitle: 'No live leads',
  emptyDescription:
    'Nothing is waiting here. Archived leads are on the Archived view, and new ones appear as people download a report.',
};

const VIEWS: readonly ViewSpec[] = [
  LIVE_VIEW,
  {
    value: 'archived',
    label: 'Archived',
    singular: 'archived lead',
    plural: 'archived leads',
    emptyTitle: 'Nothing is archived',
    emptyDescription: 'No lead has been archived. Archiving one takes it off the Live view.',
  },
  {
    value: 'all',
    label: 'All',
    singular: 'lead',
    plural: 'leads',
    emptyTitle: 'No leads yet',
    emptyDescription: 'Leads appear here as people download a report from the marketing site.',
  },
];

function viewSpecOf(view: ReportDownloadView): ViewSpec {
  return VIEWS.find((spec) => spec.value === view) ?? LIVE_VIEW;
}

/** 🔴 The tri-state, spelled once. `all` OMITS the key rather than sending `undefined`. */
function archivedParamOf(view: ReportDownloadView): { archived?: boolean } {
  if (view === 'all') return {};
  return { archived: view === 'archived' };
}

/**
 * `role="group"` with `aria-pressed`, and `max-w-full` with NO `shrink-0` — the
 * `ProposalsPanel` class string, which is the one that can wrap at 320.
 */
function ViewToggle({
  value,
  onChange,
  disabled,
}: {
  value: ReportDownloadView;
  onChange: (next: ReportDownloadView) => void;
  disabled: boolean;
}) {
  return (
    <div
      role="group"
      aria-label="Leads shown"
      className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5"
    >
      {VIEWS.map((spec) => (
        <Button
          key={spec.value}
          size="sm"
          variant={value === spec.value ? 'secondary' : 'ghost'}
          aria-pressed={value === spec.value}
          disabled={disabled}
          onClick={() => onChange(spec.value)}
        >
          {spec.label}
        </Button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Columns                                                                    */
/* -------------------------------------------------------------------------- */

interface Column {
  key: string;
  label: string;
  width: number;
  skeleton: string;
  hideLabel?: boolean;
  /** `sticky right-0` — only ever the LAST column. */
  pinned?: boolean;
}

const COLUMNS: readonly Column[] = [
  /* Who the lead is assigned to — FIRST, and code-pinned to the left edge. */
  { key: 'assignment', label: 'Assigned', width: ENQUIRY_ASSIGNMENT_COLUMN_WIDTH, skeleton: 'h-3 w-32' },
  { key: 'person', label: 'Person', width: 220, skeleton: 'h-3 w-32' },
  /* "Organisation", not "Company": it is what the visitor typed. */
  { key: 'organisation', label: 'Organisation', width: 190, skeleton: 'h-3 w-36' },
  { key: 'report', label: 'Report', width: 190, skeleton: 'h-3 w-40' },
  { key: 'campaign', label: 'UTM tags', width: 170, skeleton: 'h-3 w-28' },
  /* The SENDER's stamp — when they downloaded, not when EMERGE stored it. */
  { key: 'submitted', label: 'Submitted', width: 150, skeleton: 'h-3 w-24' },
  {
    key: 'actions',
    label: 'Actions',
    width: ENQUIRY_ACTIONS_COLUMN_WIDTH,
    skeleton: 'h-3 w-20',
    pinned: true,
  },
];
/*
 * ⚠️ The ACTIONS column is PINNED to the right edge, so it stays on screen
 * whatever the table's width — at 1330 an unpinned one sat past the right edge
 * even at 1920. The floor is summed from the VISIBLE columns' widths here, so a
 * hidden column takes its width with it.
 */

const ASSIGNMENT_KEY = 'assignment';
const ORGANISATION_KEY = 'organisation';
const ACTIONS_KEY = 'actions';
const COLUMN_KEYS: readonly string[] = COLUMNS.map((column) => column.key);
const COLUMN_BY_KEY = new Map(COLUMNS.map((column) => [column.key, column]));

/* Locked: the column that identifies the lead, and the only home of the row actions. */
const LOCKED_KEYS = [ORGANISATION_KEY, ACTIONS_KEY] as const;
/* Pinned is the edge a cell occupies — a separate fact from locked. */
const PINNED_KEYS = [ACTIONS_KEY] as const;
/* Assigned is an ordinary column, managed through the column manager like any other. */
const COLUMN_LAYOUT_SCOPE = 'enquiries.report-leads';

function lockReasonOf(key: string): string | undefined {
  if (key === ORGANISATION_KEY) return 'This column identifies the lead';
  if (key === ACTIONS_KEY) return 'Every row action lives only in this column';
  return undefined;
}

function labelOf(key: string): string {
  return COLUMN_BY_KEY.get(key)?.label ?? key;
}

/* -------------------------------------------------------------------------- */
/* Column filters                                                             */
/* -------------------------------------------------------------------------- */

type LeadSort = NonNullable<ReportDownloadLeadListParams['sort']>;

/* Exhaustive over the union, so a member added to the params type is a build error here. */
const LEAD_SORTS: Record<LeadSort, true> = {
  submittedAt: true,
  organisation: true,
  assignee: true,
  person: true,
  report: true,
  campaign: true,
};

function isLeadSort(key: string): key is LeadSort {
  return Object.prototype.hasOwnProperty.call(LEAD_SORTS, key);
}

const UTM_KINDS: readonly { prefix: string; label: string; of: (row: ReportDownloadRow) => string | null }[] = [
  { prefix: 'source', label: 'Source', of: (row) => row.utmSource },
  { prefix: 'medium', label: 'Medium', of: (row) => row.utmMedium },
  { prefix: 'campaign', label: 'Campaign', of: (row) => row.utmCampaign },
];

/* The UTM cell is a SET of three tags, so each is keyed by its kind as well as its value. */
function utmTagsOf(row: ReportDownloadRow): string[] {
  return UTM_KINDS.flatMap((kind) => {
    const value = kind.of(row);
    return value === null ? [] : [`${kind.prefix}:${value}`];
  });
}

function utmTagLabel(tag: string): string {
  const split = tag.indexOf(':');
  const kind = UTM_KINDS.find((entry) => entry.prefix === tag.slice(0, split));
  return kind === undefined ? tag : `${kind.label}: ${tag.slice(split + 1)}`;
}

/*
 * Every funnel but Assigned is page-scoped; the sort keys the API declares and
 * Assigned's one selection (`assigneeId`) reach the wire.
 * ⚠️ *SUPERSEDED 2026-10-09: "only the two sort keys the API declares"* — the
 * server sorts on assignee, person, report and campaign as well (backend
 * `32057ed`).
 */
function filterColumns(
  rows: readonly ReportDownloadRow[],
  assigneeOptions: readonly ColumnFilterOption[] | null,
): ColumnSpec<ReportDownloadRow>[] {
  const tagOptions = [...new Set(rows.flatMap(utmTagsOf))]
    .map((tag) => ({ value: tag, label: utmTagLabel(tag) }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return [
    {
      ...assignmentColumnSpec<ReportDownloadRow>(labelOf(ASSIGNMENT_KEY), assigneeOptions, rows),
      sortKey: 'assignee',
    },
    { key: 'person', label: labelOf('person'), get: (row) => row.personName, sortKey: 'person' },
    {
      key: ORGANISATION_KEY,
      label: labelOf(ORGANISATION_KEY),
      get: (row) => row.organisation,
      sortKey: 'organisation',
    },
    { key: 'report', label: labelOf('report'), get: (row) => row.reportLabel, sortKey: 'report' },
    {
      key: 'campaign',
      label: labelOf('campaign'),
      get: (row) => (utmTagsOf(row).length === 0 ? null : utmTagsOf(row).join(',')),
      options: tagOptions,
      matches: (row, selected) => utmTagsOf(row).some((tag) => selected.includes(tag)),
      sortKey: 'campaign',
    },
    {
      key: 'submitted',
      label: labelOf('submitted'),
      get: (row) => row.submittedAt,
      format: (value) => formatDateTime(value),
      sortKey: 'submittedAt',
    },
  ];
}

function HeaderCells({
  headRowRef,
  visibleKeys,
  headerFor,
  frozenSideOf,
  frozenOffsets,
}: {
  headRowRef: RefObject<HTMLTableRowElement | null>;
  visibleKeys: readonly string[];
  headerFor: (key: string) => FilterableTHProps;
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}) {
  return (
    <TR ref={headRowRef}>
      {visibleKeys.map((key) => {
        const side = frozenSideOf(key);
        if (key === ACTIONS_KEY) {
          return (
            <TH
              key={key}
              pinned={side === undefined}
              frozen={side}
              frozenOffset={frozenOffsets[key]}
              style={{ width: COLUMN_BY_KEY.get(key)?.width }}
            >
              {labelOf(key)}
            </TH>
          );
        }
        return (
          <FilterableTH
            key={key}
            {...headerFor(key)}
            frozen={side}
            frozenOffset={frozenOffsets[key]}
          />
        );
      })}
    </TR>
  );
}

const SKELETON_ROWS = 5;
/* Owner's decision: 100 per page, the API's ceiling. */
const PER_PAGE = 100;

/* -------------------------------------------------------------------------- */
/* The delete confirmation                                                    */
/* -------------------------------------------------------------------------- */

/**
 * ⛔ **It says what is lost and that the reference is freed, and promises no
 * undo.** Deleting frees `source_reference`, so a replay afterwards is stored
 * as a second lead; archiving keeps it taken.
 */
function DeleteDialog({
  row,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  row: ReportDownloadRow;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={onConfirm}
      tone="danger"
      title="Delete this lead permanently?"
      confirmLabel="Delete permanently"
      loading={busy}
      {...(error !== null ? { error } : {})}
      description={
        <>
          <p className="m-0 [overflow-wrap:anywhere]">
            This deletes the download {row.personName} of {row.organisation} made on{' '}
            {formatDateTime(row.submittedAt)} — their contact details, the report they asked for
            and the consent they gave. It can't be recovered.
          </p>
          <p className="m-0 mt-2 [overflow-wrap:anywhere]">
            It also frees the reference <strong>{row.sourceReference}</strong>. That reference is
            what stops the sending system recording this download twice, so a replay arriving
            afterwards is stored as a <strong>second lead</strong> rather than being refused.
            Archiving keeps the reference taken.
          </p>
        </>
      }
    />
  );
}

/* -------------------------------------------------------------------------- */
/* The panel                                                                  */
/* -------------------------------------------------------------------------- */

/** What a row action is asked to do. Resolves when the work has landed; rejects on failure. */
export type ReportDownloadRowAction = (row: ReportDownloadRow) => Promise<void> | void;

type ActionKind =
  | 'createCompany'
  | 'createContact'
  | 'assign'
  | 'convert'
  | 'archive'
  | 'unarchive'
  | 'delete';

export interface ReportDownloadsPanelProps {
  /** The id for this panel's heading, so a region outside it can be labelled by it. */
  headingId?: string;
  /** Create a company for a lead linked to none. Offered to `canCreate('companies')`. */
  onCreateCompany?: ReportDownloadRowAction;
  /**
   * Create a contact from the lead (owner, 2026-10-02). Offered to
   * `canCreate('contacts')`; the menu item itself is decided by
   * `EnquiryRowActions`. Same contract as `onCreateCompany`.
   */
  onCreateContact?: ReportDownloadRowAction;
  /**
   * Assign or reassign the lead. Supplied only to a `report-download.triage`
   * reader; same contract as `onCreateCompany`.
   */
  onAssign?: ReportDownloadRowAction;
  /** The Assigned funnel's options, read once by the page. `null` while unread or failed. */
  assigneeOptions?: readonly ColumnFilterOption[] | null;
  /** Raise a deal. Offered to `canCreate('deals')`, and withheld once converted. */
  onConvert?: ReportDownloadRowAction;
  /** Archive. Anyone who may read. */
  onArchive?: ReportDownloadRowAction;
  /** Put it back. Anyone who may read. */
  onUnarchive?: ReportDownloadRowAction;
  /** Destroy it. Administrators only, and only on an archived row — both applied here. */
  onDelete?: ReportDownloadRowAction;
  /**
   * Called after any row action SUCCEEDS — archive, unarchive, delete, and a
   * convert flow closing — so the page can re-read its to-deal-with counts.
   * ⚠️ A prop rather than this panel calling `useEnquirySummary` itself: the
   * page owns the ONE instance whose counts the tab pills render, and a second
   * instance here would refresh a count nothing displays.
   */
  onChanged?: () => void;
}

export function ReportDownloadsPanel({
  headingId,
  onCreateCompany,
  onCreateContact,
  onAssign,
  assigneeOptions = null,
  onConvert,
  onArchive,
  onUnarchive,
  onDelete,
  onChanged,
}: ReportDownloadsPanelProps) {
  const { ability } = useAuth();

  /* 🔴 `null` is WE COULD NOT ASK; `[]` is WE ASKED AND THERE ARE NONE. ⛔ Never `?? []`. */
  const [rows, setRows] = useState<readonly ReportDownloadRow[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [view, setView] = useRememberedState<ReportDownloadView>(
    'report-leads-view',
    LIVE_VIEW.value,
    (raw) => (raw === 'live' || raw === 'archived' || raw === 'all' ? raw : undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /* A 403, held apart so a refusal and a failure cannot share a sentence. */
  const [denied, setDenied] = useState<string | null>(null);

  const [confirmingDelete, setConfirmingDelete] = useState<ReportDownloadRow | null>(null);
  /* Which row AND which action — a spinner is a claim about one control. */
  const [busy, setBusy] = useState<{ id: number; kind: ActionKind } | null>(null);
  const [actionFailure, setActionFailure] = useState<{ id: number; message: string } | null>(null);

  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((current) => current + 1), []);

  const layout = useColumnLayout(
    COLUMN_LAYOUT_SCOPE,
    COLUMN_KEYS,
    LOCKED_KEYS,
    PINNED_KEYS,
  );
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    layout.visibleKeys,
    layout.frozenSideOf,
    PINNED_KEYS,
  );
  /* Summed from the VISIBLE columns, inline: an interpolated `min-w-[…]` emits no rule. */
  const tableMinWidth = useMemo(
    () => layout.visibleKeys.reduce((sum, key) => sum + (COLUMN_BY_KEY.get(key)?.width ?? 0), 0),
    [layout.visibleKeys],
  );

  const loadedRows = useMemo(() => (rows === null ? [] : [...rows]), [rows]);
  const columns = useMemo(
    () => filterColumns(loadedRows, assigneeOptions),
    [loadedRows, assigneeOptions],
  );
  const filterKeys = useMemo(() => columns.map((column) => column.key), [columns]);
  const filters = useTableFilters(loadedRows, columns);
  const visibleRows = filters.visibleRows;
  /* The Assigned funnel's one server selection; `headerFor` below resets the page on it. */
  const assigneeId = assigneeFilterOf(filters.serverParams[ASSIGNEE_PARAM]);
  /* ...and so does "Clear column filters", which bypasses `headerFor`: adjusted during render. */
  const [assigneeSeen, setAssigneeSeen] = useState(assigneeId);
  if (assigneeSeen !== assigneeId) {
    setAssigneeSeen(assigneeId);
    setPage(1);
  }
  const sortKey = filters.sort !== null && isLeadSort(filters.sort.key) ? filters.sort.key : null;
  const sortDirection = filters.sort?.direction ?? null;

  /* A sort re-queries, so it starts again at page 1. The funnels filter the loaded page. */
  function headerFor(key: string): FilterableTHProps {
    const props = filters.headerFor(key);
    const filter = props.filter;
    if (!filter) return props;
    const sortControl = filter.sort;
    return {
      ...props,
      filter: {
        ...filter,
        onChange: (next) => {
          filter.onChange(next);
          if (filter.scope === 'server') setPage(1);
        },
        ...(sortControl
          ? {
              sort: {
                direction: sortControl.direction,
                onSort: (direction) => {
                  sortControl.onSort(direction);
                  setPage(1);
                },
              },
            }
          : {}),
      },
    };
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    api
      .listReportDownloadLeads(
        {
          page,
          perPage: PER_PAGE,
          ...archivedParamOf(view),
          ...(sortKey !== null && sortDirection !== null
            ? { sort: sortKey, order: sortDirection }
            : {}),
          ...(assigneeId !== null ? { assigneeId } : {}),
        },
        controller.signal,
      )
      .then((answer) => {
        if (controller.signal.aborted) return;
        const pages = Math.max(answer.meta.totalPages, 1);
        /* The stranded page: re-read the last real page rather than painting past the end. */
        if (page > pages) {
          setTotalPages(pages);
          setPage(pages);
          return;
        }
        setRows(answer.data.map(rowOf));
        setTotal(answer.meta.total);
        setTotalPages(pages);
        setError(null);
        setDenied(null);
        setLoading(false);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        /* ⛔ The rows and the count go back to `null` together. */
        setRows(null);
        setTotal(null);
        setLoading(false);
        if (caught instanceof ApiError && caught.status === 403) {
          setDenied(caught.message);
          setError(null);
          return;
        }
        setDenied(null);
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'The leads couldn’t be loaded. Check your connection and try again.',
        );
      });

    return () => controller.abort();
  }, [page, view, reloadKey, sortKey, sortDirection, assigneeId]);

  /* No AbortSignal is passed down: aborting a write cancels the listening, not the write. */
  const run = useCallback(
    (row: ReportDownloadRow, kind: ActionKind, action: ReportDownloadRowAction, after?: () => void) => {
      setBusy({ id: row.id, kind });
      setActionFailure(null);
      Promise.resolve()
        .then(() => action(row))
        .then(() => {
          setBusy(null);
          after?.();
          reload();
          onChanged?.();
        })
        .catch((caught: unknown) => {
          setBusy(null);
          setActionFailure({
            id: row.id,
            message: caught instanceof ApiError ? caught.message : 'That didn’t go through. Try again.',
          });
        });
    },
    [reload, onChanged],
  );

  /* 🔴 Back to page 1 on every view change; re-selecting the current pill does nothing. */
  const changeView = useCallback(
    (next: ReportDownloadView) => {
      if (next === view) return;
      setView(next);
      setPage(1);
      setActionFailure(null);
    },
    [view, setView],
  );

  const spec = viewSpecOf(view);
  const firstRow = total === null || total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === null ? 0 : Math.min(page * PER_PAGE, total);

  /* A delete failure is reported once, in the dialog that is over it. */
  const failureFor = (row: ReportDownloadRow): string | null =>
    actionFailure?.id === row.id && confirmingDelete?.id !== row.id ? actionFailure.message : null;
  const busyKindFor = (row: ReportDownloadRow): ActionKind | null =>
    busy?.id === row.id ? busy.kind : null;

  const actionsFor = (row: ReportDownloadRow) => (
    <RowActions
      row={row}
      mayDelete={ability.canDelete('report-download')}
      canConvert={ability.canCreate('deals')}
      canCreateCompany={ability.canCreate('companies')}
      canCreateContact={ability.canCreate('contacts')}
      busyKind={busyKindFor(row)}
      anyBusy={busy !== null}
      failure={failureFor(row)}
      {...(onCreateCompany
        ? { onCreateCompany: () => run(row, 'createCompany', onCreateCompany) }
        : {})}
      {...(onCreateContact
        ? { onCreateContact: () => run(row, 'createContact', onCreateContact) }
        : {})}
      {...(onAssign ? { onAssign: () => run(row, 'assign', onAssign) } : {})}
      {...(onConvert ? { onConvert: () => run(row, 'convert', onConvert) } : {})}
      {...(onArchive ? { onArchive: () => run(row, 'archive', onArchive) } : {})}
      {...(onUnarchive ? { onUnarchive: () => run(row, 'unarchive', onUnarchive) } : {})}
      {...(onDelete ? { onDelete: () => setConfirmingDelete(row) } : {})}
    />
  );

  return (
    <Panel {...(headingId !== undefined ? { 'aria-labelledby': headingId } : {})}>
      <PanelHeader
        {...(headingId !== undefined ? { id: headingId } : {})}
        title="Report leads"
        /* 🔴 The count renders only over an array, names its population, and is in no live region. */
        actions={
          rows === null ? (
            loading ? (
              <Skeleton className="h-3 w-24" />
            ) : null
          ) : total === null ? null : (
            <span className="text-xs text-fg-muted tabular">
              {pluralise(total, spec.singular, spec.plural)}
            </span>
          )
        }
      />

      {denied === null && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          <ViewToggle value={view} onChange={changeView} disabled={loading} />
          {/* Below `md` there is no table to arrange; the funnels move into a sheet. */}
          <div className="md:hidden">
            <FilterSheet
              keys={filterKeys}
              headerFor={headerFor}
              activeCount={filters.activeCount}
              clearAll={filters.clearAll}
            />
          </div>
          <div className="hidden md:block">
            <ColumnManager
              layout={layout}
              labelOf={labelOf}
              lockedKeys={LOCKED_KEYS}
              lockReasonOf={lockReasonOf}
              pinnedKeys={PINNED_KEYS}
            />
          </div>
        </div>
      )}

      {error !== null && (
        <div className="border-b border-border px-4 py-3">
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load leads.</>}>
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        </div>
      )}

      {denied !== null ? (
        <EmptyState
          icon={<EventInterestIcon />}
          title="Report leads are restricted"
          description={
            denied === ''
              ? 'Your account is not allowed to read report-download leads.'
              : `Your account is not allowed to read report-download leads. ${denied}`
          }
        />
      ) : loading && rows === null ? (
        <>
          <div className="md:hidden">
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <div key={index} className="border-b border-border px-4 py-3 last:border-b-0">
                <Skeleton className="h-3 w-36" />
                <Skeleton className="mt-2 h-2.5 w-28" />
                <Skeleton className="mt-2 h-2.5 w-full" />
              </div>
            ))}
          </div>
          <div className="hidden md:block">
            <TableWrap frozenHeader>
              {/* The floor goes through `style`: an interpolated `min-w-[…]` emits no rule. */}
              <Table style={{ minWidth: tableMinWidth }}>
                <THead>
                  <HeaderCells
                    headRowRef={headRowRef}
                    visibleKeys={layout.visibleKeys}
                    headerFor={headerFor}
                    frozenSideOf={layout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                </THead>
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <TR key={index}>
                      {layout.visibleKeys.map((key) => {
                        const side = layout.frozenSideOf(key);
                        return (
                          <TD
                            key={key}
                            pinned={key === ACTIONS_KEY && side === undefined}
                            frozen={side}
                            frozenOffset={frozenOffsets[key]}
                          >
                            <Skeleton className={COLUMN_BY_KEY.get(key)?.skeleton ?? 'h-3 w-20'} />
                          </TD>
                        );
                      })}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          </div>
        </>
      ) : rows === null ? (
        <EmptyState
          icon={<EventInterestIcon />}
          title="Nothing to show"
          description="The leads couldn’t be loaded. Check the message above and try again."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<EventInterestIcon />}
          title={spec.emptyTitle}
          description={spec.emptyDescription}
        />
      ) : visibleRows.length === 0 ? (
        /* Leads exist on this page; the column filters hid every one. */
        <EmptyState
          icon={<EventInterestIcon />}
          title="No leads match the column filters"
          action={
            <Button variant="secondary" size="sm" onClick={filters.clearAll}>
              Clear column filters
            </Button>
          }
        />
      ) : (
        <>
          {/* Below `md`: a card per lead, carrying every fact the table does. */}
          <div className="md:hidden">
            {visibleRows.map((row) => (
              <article key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                {/* The Assigned column leads the table, so it leads the card. */}
                <div className="mb-2 min-w-0">
                  <EnquiryAssignmentCell assignment={row.assignment} />
                </div>
                <OrganisationCell row={row} />
                <div className="mt-2">
                  <PersonCell row={row} />
                </div>
                <div className="mt-2">
                  <ReportCell row={row} />
                </div>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  <UtmTerms row={row} />
                  <dt className="m-0 text-fg-muted">Submitted</dt>
                  <dd className="m-0 text-fg-secondary">{formatDateTime(row.submittedAt)}</dd>
                </dl>
                <div className="mt-2">{actionsFor(row)}</div>
              </article>
            ))}
          </div>

          <div className="hidden md:block">
            <TableWrap frozenHeader>
              <Table style={{ minWidth: tableMinWidth }}>
                <THead>
                  <HeaderCells
                    headRowRef={headRowRef}
                    visibleKeys={layout.visibleKeys}
                    headerFor={headerFor}
                    frozenSideOf={layout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                </THead>
                <TBody>
                  {visibleRows.map((row) => (
                    <TR key={row.id}>
                      {layout.visibleKeys.map((key) => {
                        const side = layout.frozenSideOf(key);
                        return (
                          <TD
                            key={key}
                            pinned={key === ACTIONS_KEY && side === undefined}
                            frozen={side}
                            frozenOffset={frozenOffsets[key]}
                          >
                            {key === ACTIONS_KEY ? actionsFor(row) : <LeadCell columnKey={key} row={row} />}
                          </TD>
                        );
                      })}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
              <span className="text-xs text-fg-muted tabular">
                {total === null
                  ? `Page ${page}`
                  : visibleRows.length < rows.length
                    ? `Showing ${visibleRows.length} of rows ${firstRow}–${lastRow} · ${total} in total`
                    : `${firstRow}–${lastRow} of ${total}`}
              </span>
              <span className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => Math.max(current - 1, 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                >
                  Next
                </Button>
              </span>
            </div>
          )}
        </>
      )}

      {confirmingDelete !== null && onDelete && (
        <DeleteDialog
          row={confirmingDelete}
          busy={busy?.id === confirmingDelete.id && busy.kind === 'delete'}
          error={actionFailure?.id === confirmingDelete.id ? actionFailure.message : null}
          onClose={() => {
            setConfirmingDelete(null);
            setActionFailure(null);
          }}
          onConfirm={() => {
            const row = confirmingDelete;
            run(row, 'delete', onDelete, () => setConfirmingDelete(null));
          }}
        />
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/* Cells                                                                      */
/* -------------------------------------------------------------------------- */

/** One data cell, by column key — so the body follows the reader's column order. */
function LeadCell({ columnKey, row }: { columnKey: string; row: ReportDownloadRow }) {
  switch (columnKey) {
    case ASSIGNMENT_KEY:
      return (
        <div className="min-w-0">
          <EnquiryAssignmentCell assignment={row.assignment} />
        </div>
      );
    case 'person':
      return <PersonCell row={row} />;
    case ORGANISATION_KEY:
      return <OrganisationCell row={row} />;
    case 'report':
      return <ReportCell row={row} />;
    case 'campaign':
      return (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
          <UtmTerms row={row} />
        </dl>
      );
    case 'submitted':
      return (
        <span className="text-sm text-fg-secondary">{formatDateTime(row.submittedAt)}</span>
      );
    default:
      return null;
  }
}

/** Rendered as it arrived: the email not lower-cased, the phone with no dial code guessed. */
function PersonCell({ row }: { row: ReportDownloadRow }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-sm text-fg [overflow-wrap:anywhere]">{row.personName}</span>
      <EmailLink address={row.email} tone="muted" className="text-xs" />
      {row.designation !== null && (
        <span className="text-xs text-fg-subtle [overflow-wrap:anywhere]">{row.designation}</span>
      )}
      {row.phone !== null && (
        <span className="text-xs text-fg-subtle [overflow-wrap:anywhere]">{row.phone}</span>
      )}
    </span>
  );
}

/** The organisation as typed, with the `Archived` marker as a real word on every archived row. */
function OrganisationCell({ row }: { row: ReportDownloadRow }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="text-sm font-medium text-fg [overflow-wrap:anywhere]">
        {row.organisation}
      </span>
      {row.archived && (
        <Badge tone="neutral" title="Somebody took this off the Live view.">
          Archived
        </Badge>
      )}
    </span>
  );
}

/** The report's title, or its slug where none was sent. The slug rides on `title` for both. */
function ReportCell({ row }: { row: ReportDownloadRow }) {
  return (
    <span className="text-sm text-fg-secondary [overflow-wrap:anywhere]" title={row.contentSlug}>
      {row.reportLabel}
    </span>
  );
}

/**
 * Source / Medium / Campaign, always all three so the rows line up.
 * ⛔ **A tag nobody sent is an em dash** — never "Direct" or "Unknown".
 */
function UtmTerms({ row }: { row: ReportDownloadRow }) {
  const terms: readonly [string, string | null][] = [
    ['Source', row.utmSource],
    ['Medium', row.utmMedium],
    ['Campaign', row.utmCampaign],
  ];
  return (
    <>
      {terms.map(([label, value]) => (
        <UtmTerm key={label} label={label} value={value} />
      ))}
    </>
  );
}

function UtmTerm({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <dt className="m-0 text-fg-muted">{label}</dt>
      <dd
        className="m-0 truncate text-fg-secondary"
        {...(value !== null ? { title: value } : {})}
      >
        {value ?? '—'}
      </dd>
    </>
  );
}

/**
 * The row's controls. **Every gate is here.** Withheld rather than disabled in
 * every case, and an action with no handler is withheld too.
 */
function RowActions({
  row,
  mayDelete,
  canConvert,
  canCreateCompany,
  canCreateContact,
  busyKind,
  anyBusy,
  failure,
  onCreateCompany,
  onCreateContact,
  onAssign,
  onConvert,
  onArchive,
  onUnarchive,
  onDelete,
}: {
  row: ReportDownloadRow;
  /**
   * `canDelete('report-download')` — 2026-10-01, deletes follow the set.
   * *SUPERSEDED: "Deliberately NOT `canDelete('report-download')` — the owner's
   * tiers are non-uniform."*
   */
  mayDelete: boolean;
  /** The convert writes a DEAL, so the deal's module decides it. */
  canConvert: boolean;
  /** Creating writes a COMPANY, so the companies module decides it. */
  canCreateCompany: boolean;
  /** Creating writes a CONTACT, so the contacts module decides it. */
  canCreateContact: boolean;
  busyKind: ActionKind | null;
  anyBusy: boolean;
  failure: string | null;
  onCreateCompany?: () => void;
  onCreateContact?: () => void;
  /** Supplied only to a triager — the page passes `onAssign` inside its triage block. */
  onAssign?: () => void;
  onConvert?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete?: () => void;
}) {
  /* One Actions menu (owner, 2026-10-01); every gate unchanged. */
  const { ability } = useAuth();
  const items: EnquiryActionItem[] = [];
  if (onAssign) {
    items.push({
      key: 'assign',
      label: row.assignment ? 'Reassign…' : 'Assign to…',
      onSelect: onAssign,
      busy: busyKind === 'assign',
    });
  }
  if (row.company === null && canCreateCompany && onCreateCompany) {
    items.push({
      key: 'company',
      label: 'Create a company',
      onSelect: onCreateCompany,
      busy: busyKind === 'createCompany',
    });
  }
  if (row.convertible && canConvert && onConvert) {
    items.push({
      key: 'convert',
      label: 'Convert to a deal',
      onSelect: onConvert,
      busy: busyKind === 'convert',
    });
  }
  if (!row.archived && onArchive) {
    items.push({ key: 'archive', label: 'Archive', onSelect: onArchive, busy: busyKind === 'archive' });
  }
  if (row.archived && onUnarchive) {
    items.push({
      key: 'unarchive',
      label: 'Unarchive',
      onSelect: onUnarchive,
      busy: busyKind === 'unarchive',
    });
  }
  /* Both gates: the delete grant, and a row already archived. */
  if (row.archived && mayDelete && onDelete) {
    items.push({ key: 'delete', label: 'Delete permanently', onSelect: onDelete, danger: true });
  }

  return (
    <EnquiryRowActions
      rowName={row.organisation}
      company={row.company}
      /* Two different facts; "Deal raised" is false about the second. */
      deal={
        row.convertible
          ? null
          : row.dealId !== null
            ? { kind: 'raised', id: row.dealId }
            : { kind: 'deleted' }
      }
      /* The item itself is decided by EnquiryRowActions; not pushed into `items`. */
      contact={
        onCreateContact
          ? {
              email: row.email,
              canCreate: canCreateContact,
              /* On a row with no company the flow links one, which is triage. */
              canLinkCompany: ability.canTriage('report-download'),
              onCreate: onCreateContact,
              busy: busyKind === 'createContact',
            }
          : null
      }
      items={items}
      disabled={anyBusy}
      failure={failure}
    />
  );
}
